import { usePublicClient, useAccount } from 'wagmi';
import { useQuery } from '@tanstack/react-query';
import { publicActionsL2 } from 'viem/op-stack';
import { DEFAULT_WAGMI_QUERY_GAS_PRICE_STALE_TIME } from '@/onchain/querySettings';

const QUERY_KEY_GAS_PRICE = ['useGasPrice'] as const;

export type GasPriceData = {
  maxPriorityFeePerGas: bigint;
  maxFeePerGas: bigint;
  l1DataFee: bigint;
};

/**
 * Hook to fetch the current gas fees for L2 execution and L1 data fee, using EIP-1559 pricing
 * @param enabled - Optional flag to conditionally enable/disable fetching
 * @returns Object containing the current gas fees
 * @throws Will throw if the RPC request fails
 */
export function useGasPrice(enabled = true) {
  const publicClient = usePublicClient();
  if (!publicClient) {
    throw new Error('No RPC client found for network');
  }
  const publicClientWithL2Actions = publicClient.extend(publicActionsL2());

  const account = useAccount();

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY_GAS_PRICE,
    queryFn: async () => {
      const l2Fees = await publicClient.estimateFeesPerGas();
      const l1Fees = await publicClientWithL2Actions.estimateL1Fee({
        account: account.address ?? '0x0000000000000000000000000000000000000000',
      });

      return {
        maxPriorityFeePerGas: l2Fees.maxPriorityFeePerGas ?? 0n,
        maxFeePerGas: l2Fees.maxFeePerGas ?? 0n,
        l1DataFee: l1Fees,
      } satisfies GasPriceData;
    },
    // Cache gas prices for 20 seconds and reduce refetching
    staleTime: DEFAULT_WAGMI_QUERY_GAS_PRICE_STALE_TIME,
    gcTime: DEFAULT_WAGMI_QUERY_GAS_PRICE_STALE_TIME * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: enabled, // Control whether the query executes
  });

  return {
    priceData: data,
    isLoading,
    error,
  };
}
